import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { supabase } from '../../lib/supabase';

// Legacy role config kept only as fallback for profile display
const LEGACY_ROLE_CONFIG = {
  admin: {
    label: 'Admin',
    icon: 'ShieldCheck',
    color: 'text-accent',
    bg: 'bg-accent/15',
    description: 'Full access including user management and financial settings',
  },
  estimator: {
    label: 'Estimator',
    icon: 'Briefcase',
    color: 'text-primary',
    bg: 'bg-primary/10',
    description: 'Can manage projects and cost estimation, no user/financial access',
  },
  viewer: {
    label: 'Viewer',
    icon: 'Eye',
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    description: 'Read-only access to dashboards and reports',
  },
};

// RBAC role display config — mirrors Roles & Permissions master page
const RBAC_ROLE_CONFIG = {
  SUPER_ADMIN: { label: 'Super Admin', icon: 'Crown', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30', description: 'Unrestricted access to all modules and system settings' },
  ADMIN: { label: 'Admin', icon: 'ShieldCheck', color: 'text-violet-500', bg: 'bg-violet-500/10', border: 'border-violet-500/30', description: 'Full access including user management and financial settings' },
  SALES_USER: { label: 'Sales User', icon: 'TrendingUp', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30', description: 'Access to service calculator and own deals' },
  SALES_MANAGER: { label: 'Sales Manager', icon: 'BarChart2', color: 'text-indigo-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', description: 'Manages sales team, deals, and performance reports' },
  PROJECT_MANAGER: { label: 'Project Manager', icon: 'FolderKanban', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/30', description: 'Manages project incentive tickets and team assignments' },
  APPROVER: { label: 'Approver', icon: 'ClipboardCheck', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30', description: 'Reviews and approves or rejects submitted tickets' },
  FINANCE: { label: 'Finance', icon: 'Banknote', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', description: 'Manages payments and financial reports' },
  HR_MASTER_DATA: { label: 'HR / Master Data', icon: 'Database', color: 'text-teal-500', bg: 'bg-teal-500/10', border: 'border-teal-500/30', description: 'Manages master data including engineers and resources' },
  EMPLOYEE: { label: 'Employee', icon: 'User', color: 'text-sky-500', bg: 'bg-sky-500/10', border: 'border-sky-500/30', description: 'View-only access to personal incentive reports' },
  AUDITOR: { label: 'Auditor', icon: 'Search', color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/30', description: 'Read-only access to audit logs and reports' },
};

// Module display config — mirrors Roles & Permissions master page
const MODULE_DISPLAY = {
  service_calculator: { label: 'Service Calculator', icon: 'Calculator', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  saved_calculations: { label: 'Saved Calculations / Deals', icon: 'BookOpen', color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
  incentive_tickets: { label: 'Project Incentive Tickets', icon: 'Ticket', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  team_assignment: { label: 'Team Assignment', icon: 'UserPlus', color: 'text-purple-500', bg: 'bg-purple-500/10' },
  pending_approval: { label: 'Pending Approval', icon: 'ClipboardCheck', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  finance_payments: { label: 'Finance Payments', icon: 'Banknote', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  finance_payment_report: { label: 'Finance Payment Report', icon: 'FileText', color: 'text-teal-500', bg: 'bg-teal-500/10' },
  employee_incentive_reports: { label: 'Employee Incentive Reports', icon: 'BarChart2', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  analytics_dashboard: { label: 'Analytics Dashboard', icon: 'BarChart3', color: 'text-sky-500', bg: 'bg-sky-500/10' },
  master_data: { label: 'Master Data', icon: 'Database', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  users: { label: 'Users', icon: 'Users', color: 'text-rose-500', bg: 'bg-rose-500/10' },
  settings: { label: 'Settings', icon: 'Settings', color: 'text-slate-500', bg: 'bg-slate-500/10' },
  roles_permissions: { label: 'Roles & Permissions', icon: 'ShieldCheck', color: 'text-red-500', bg: 'bg-red-500/10' },
};

const PERM_LABELS = {
  view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete',
  submit: 'Submit', approve: 'Approve', reject: 'Reject', hold: 'Hold',
  return_for_edit: 'Return', mark_close_won: 'Close Won', create_incentive_ticket: 'Create Ticket',
  add_team_member: 'Add Member', remove_team_member: 'Remove Member', validate_calculate: 'Calculate',
  mark_ready_for_payment: 'Ready for Pay', mark_paid: 'Mark Paid', export_pdf: 'Export PDF',
  export_excel: 'Export Excel', print: 'Print', manage_users: 'Manage Users',
  manage_roles: 'Manage Roles', manage_settings: 'Manage Settings', view_all: 'View All',
  view_own: 'View Own', view_team: 'View Team', view_finance_only: 'Finance Only',
  reset_password: 'Reset Password',
};

const AccountManagement = () => {
  const { user, userProfile, updateProfile, isAdmin, permissions } = useAuth();
  const { rbacRoles, rbacRoleKeys, rbacPermissions, rbacLoading, isAdmin: isRbacAdmin } = useRBAC();

  const [activeTab, setActiveTab] = useState('profile');

  // Users tab state (Admin only) — kept for backward compat but hidden from tabs
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersSuccess, setUsersSuccess] = useState('');
  const [roleActionLoading, setRoleActionLoading] = useState(null);
  const [userSearch, setUserSearch] = useState('');

  // Add User Modal state
  const [addUserModal, setAddUserModal] = useState(false);
  const [addUserData, setAddUserData] = useState({ email: '', password: '', full_name: '', phone: '', role: 'estimator' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');

  // Edit User Modal state
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({ id: '', full_name: '', email: '', phone: '', role: 'estimator', is_active: true });
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState('');

  // Delete User Confirmation state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState({
    full_name: userProfile?.full_name || user?.email?.split('@')?.[0] || '',
    display_name: userProfile?.display_name || '',
    phone: userProfile?.phone || '',
    department: userProfile?.department || '',
    job_title: userProfile?.job_title || '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  // Auth preferences state
  const [authPrefs, setAuthPrefs] = useState({
    email_notifications: userProfile?.email_notifications ?? true,
    session_timeout: userProfile?.session_timeout || '8h',
    two_factor_enabled: userProfile?.two_factor_enabled ?? false,
  });
  const [authPrefsLoading, setAuthPrefsLoading] = useState(false);
  const [authPrefsSuccess, setAuthPrefsSuccess] = useState('');
  const [authPrefsError, setAuthPrefsError] = useState('');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User', show: true },
    { id: 'password', label: 'Password', icon: 'Lock', show: true },
    { id: 'preferences', label: 'Auth Preferences', icon: 'Shield', show: true },
    { id: 'my-permissions', label: 'My Permissions', icon: 'ShieldCheck', show: true },
    // Users tab removed — user management is centralized in Roles & Permissions
  ]?.filter(t => t?.show);

  // Load users when Users tab is active
  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      fetchUsers();
    }
  }, [activeTab, isAdmin]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      setUsersError(err?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRoleChange = async (targetUser, newRole) => {
    if (targetUser?.id === user?.id) return;
    setRoleActionLoading(targetUser?.id);
    setUsersSuccess('');
    setUsersError('');
    try {
      const { error } = await supabase?.rpc('set_user_role', {
        target_email: targetUser?.email,
        new_role: newRole,
      });
      if (error) throw error;
      setUsers(prev => prev?.map(u => u?.id === targetUser?.id ? { ...u, role: newRole } : u));
      setUsersSuccess(`${targetUser?.email} role updated to ${LEGACY_ROLE_CONFIG?.[newRole]?.label}`);
      setTimeout(() => setUsersSuccess(''), 3500);
    } catch (err) {
      setUsersError(err?.message || 'Failed to update role');
    } finally {
      setRoleActionLoading(null);
    }
  };

  const handleToggleActive = async (targetUser) => {
    if (targetUser?.id === user?.id) return;
    setRoleActionLoading(`active-${targetUser?.id}`);
    setUsersSuccess('');
    setUsersError('');
    try {
      const newActive = !targetUser?.is_active;
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: newActive, updated_at: new Date()?.toISOString() })?.eq('id', targetUser?.id);
      if (error) throw error;
      setUsers(prev => prev?.map(u => u?.id === targetUser?.id ? { ...u, is_active: newActive } : u));
      setUsersSuccess(`${targetUser?.email} has been ${newActive ? 'activated' : 'deactivated'}`);
      setTimeout(() => setUsersSuccess(''), 3500);
    } catch (err) {
      setUsersError(err?.message || 'Failed to update status');
    } finally {
      setRoleActionLoading(null);
    }
  };

  // Add User handler
  const handleOpenAddUser = () => {
    setAddUserData({ email: '', password: '', full_name: '', phone: '', role: 'estimator' });
    setAddUserError('');
    setAddUserModal(true);
  };

  const handleAddUser = async (e) => {
    e?.preventDefault();
    setAddUserError('');

    if (!addUserData?.email?.trim()) { setAddUserError('Email is required.'); return; }
    if (!addUserData?.password || addUserData?.password?.length < 8) { setAddUserError('Password must be at least 8 characters.'); return; }
    if (!addUserData?.full_name?.trim()) { setAddUserError('Full name is required.'); return; }

    setAddUserLoading(true);
    try {
      const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
        email: addUserData?.email?.trim()?.toLowerCase(),
        password: addUserData?.password,
        options: {
          data: { full_name: addUserData?.full_name?.trim() }
        }
      });

      if (signUpError) throw signUpError;

      const newUserId = signUpData?.user?.id;

      if (newUserId) {
        const { error: profileErr } = await supabase?.from('user_profiles')?.upsert({
          id: newUserId,
          email: addUserData?.email?.trim()?.toLowerCase(),
          full_name: addUserData?.full_name?.trim(),
          phone: addUserData?.phone?.trim() || null,
          role: addUserData?.role,
          is_active: true,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'id' });

        if (profileErr) throw profileErr;

        if (addUserData?.role !== 'viewer') {
          await supabase?.rpc('set_user_role', {
            target_email: addUserData?.email?.trim()?.toLowerCase(),
            new_role: addUserData?.role,
          });
        }
      }

      setUsersSuccess(`User ${addUserData?.email} created successfully as ${LEGACY_ROLE_CONFIG?.[addUserData?.role]?.label}.`);
      setTimeout(() => setUsersSuccess(''), 4000);
      setAddUserModal(false);
      await fetchUsers();
    } catch (err) {
      setAddUserError(err?.message || 'Failed to create user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  // Edit User handler
  const handleOpenEditUser = (targetUser) => {
    setEditUserData({
      id: targetUser?.id,
      full_name: targetUser?.full_name || '',
      email: targetUser?.email || '',
      phone: targetUser?.phone || '',
      role: targetUser?.role || 'estimator',
      is_active: targetUser?.is_active ?? true,
    });
    setEditUserError('');
    setEditUserModal(true);
  };

  const handleEditUser = async (e) => {
    e?.preventDefault();
    setEditUserError('');

    if (!editUserData?.full_name?.trim()) { setEditUserError('Full name is required.'); return; }

    setEditUserLoading(true);
    try {
      const { error: updateErr } = await supabase?.from('user_profiles')?.update({
        full_name: editUserData?.full_name?.trim(),
        phone: editUserData?.phone?.trim() || null,
        role: editUserData?.role,
        is_active: editUserData?.is_active,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', editUserData?.id);

      if (updateErr) throw updateErr;

      // Update role via RPC if changed
      const originalUser = users?.find(u => u?.id === editUserData?.id);
      if (originalUser?.role !== editUserData?.role) {
        await supabase?.rpc('set_user_role', {
          target_email: editUserData?.email,
          new_role: editUserData?.role,
        });
      }

      setUsers(prev => prev?.map(u => u?.id === editUserData?.id ? {
        ...u,
        full_name: editUserData?.full_name?.trim(),
        phone: editUserData?.phone?.trim() || null,
        role: editUserData?.role,
        is_active: editUserData?.is_active,
      } : u));

      setUsersSuccess(`User ${editUserData?.email} updated successfully.`);
      setTimeout(() => setUsersSuccess(''), 4000);
      setEditUserModal(false);
    } catch (err) {
      setEditUserError(err?.message || 'Failed to update user. Please try again.');
    } finally {
      setEditUserLoading(false);
    }
  };

  // Delete User handler
  const handleOpenDeleteConfirm = (targetUser) => {
    setDeleteTargetUser(targetUser);
    setDeleteConfirmModal(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteTargetUser) return;
    setDeleteLoading(true);
    setUsersError('');
    try {
      // Soft delete: deactivate the user profile
      const { error } = await supabase?.from('user_profiles')?.update({
        is_active: false,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', deleteTargetUser?.id);

      if (error) throw error;

      setUsers(prev => prev?.filter(u => u?.id !== deleteTargetUser?.id));
      setUsersSuccess(`User ${deleteTargetUser?.email} has been removed from the system.`);
      setTimeout(() => setUsersSuccess(''), 4000);
      setDeleteConfirmModal(false);
      setDeleteTargetUser(null);
    } catch (err) {
      setUsersError(err?.message || 'Failed to delete user.');
      setDeleteConfirmModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredUsers = users?.filter(u =>
    !userSearch || u?.email?.toLowerCase()?.includes(userSearch?.toLowerCase()) ||
    u?.full_name?.toLowerCase()?.includes(userSearch?.toLowerCase())
  );

  const getInitials = (name) =>
    name?.split(' ')?.map(w => w?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || '?';

  // Profile handlers
  const handleProfileChange = (e) => {
    const { name, value } = e?.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e?.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    const { error } = await updateProfile(profileData);
    if (error) {
      setProfileError(error?.message || 'Failed to update profile. Please try again.');
    } else {
      setProfileSuccess('Profile updated successfully.');
    }
    setProfileLoading(false);
  };

  // Password handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordData?.current_password) {
      setPasswordError('Current password is required.');
      return;
    }
    if (passwordData?.new_password?.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (passwordData?.new_password !== passwordData?.confirm_password) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    const { error: signInError } = await supabase?.auth?.signInWithPassword({
      email: user?.email,
      password: passwordData?.current_password,
    });

    if (signInError) {
      setPasswordError('Current password is incorrect.');
      setPasswordLoading(false);
      return;
    }

    const { error } = await supabase?.auth?.updateUser({ password: passwordData?.new_password });
    if (error) {
      setPasswordError(error?.message || 'Failed to update password. Please try again.');
    } else {
      setPasswordSuccess('Password updated successfully.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    }
    setPasswordLoading(false);
  };

  // Auth preferences handlers
  const handleAuthPrefsChange = (key, value) => {
    setAuthPrefs(prev => ({ ...prev, [key]: value }));
    setAuthPrefsError('');
    setAuthPrefsSuccess('');
  };

  const handleAuthPrefsSubmit = async (e) => {
    e?.preventDefault();
    setAuthPrefsLoading(true);
    setAuthPrefsError('');
    setAuthPrefsSuccess('');
    const { error } = await updateProfile(authPrefs);
    if (error) {
      setAuthPrefsError(error?.message || 'Failed to save preferences. Please try again.');
    } else {
      setAuthPrefsSuccess('Authentication preferences saved.');
    }
    setAuthPrefsLoading(false);
  };

  // ---- Derive RBAC role display ----
  // Primary: use RBAC roles from the Roles & Permissions master page
  // Fallback: use legacy role from user_profiles
  const primaryRbacRoleKey = rbacRoleKeys?.[0] || null;
  const rbacRoleConf = primaryRbacRoleKey ? RBAC_ROLE_CONFIG?.[primaryRbacRoleKey] : null;
  const legacyRoleConf = LEGACY_ROLE_CONFIG?.[userProfile?.role] || LEGACY_ROLE_CONFIG?.viewer;
  // Use RBAC role if available, otherwise fall back to legacy
  const currentRoleConfig = rbacRoleConf || legacyRoleConf;
  const currentRoleLabel = rbacRoleConf ? rbacRoleConf?.label : legacyRoleConf?.label;
  const currentRoleKey = primaryRbacRoleKey || userProfile?.role || 'viewer';

  // ---- Build accessible modules list from rbacPermissions ----
  const accessibleModules = Object.entries(rbacPermissions || {})?.filter(([, perms]) =>
    Object.values(perms || {})?.some(v => v === true)
  );

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Account Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your profile, security, and authentication settings</p>
        </div>

        {/* Profile Summary Card */}
        <div className="bg-card border border-border rounded-xl p-6 flex items-center gap-5">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground text-xl font-bold flex-shrink-0">
            {getInitials(profileData?.full_name || user?.email?.split('@')?.[0] || 'U')}
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-foreground">
              {profileData?.full_name || user?.email?.split('@')?.[0] || 'User'}
            </div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-success inline-block"></span>
                <span className="text-xs text-muted-foreground">Active account</span>
              </div>
              {/* Show all RBAC roles as badges */}
              {rbacRoleKeys?.length > 0 ? (
                rbacRoleKeys?.map(rk => {
                  const rc = RBAC_ROLE_CONFIG?.[rk];
                  if (!rc) return null;
                  return (
                    <span key={rk} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${rc?.bg} ${rc?.color}`}>
                      <Icon name={rc?.icon} size={11} />
                      {rc?.label}
                    </span>
                  );
                })
              ) : (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${legacyRoleConf?.bg} ${legacyRoleConf?.color}`}>
                  <Icon name={legacyRoleConf?.icon} size={11} />
                  {legacyRoleConf?.label}
                </span>
              )}
            </div>
          </div>
          {/* Sync indicator */}
          <div className="flex items-center gap-1.5 text-xs text-success flex-shrink-0">
            <Icon name="RefreshCw" size={12} />
            <span>Synced with Roles & Permissions</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-smooth border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-5">
                <div>
                  <h2 className="text-base font-semibold text-foreground mb-1">Personal Information</h2>
                  <p className="text-sm text-muted-foreground">Update your name, contact details, and role information.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    name="full_name"
                    value={profileData?.full_name}
                    onChange={handleProfileChange}
                    placeholder="Enter your full name"
                  />
                  <Input
                    label="Display Name"
                    name="display_name"
                    value={profileData?.display_name}
                    onChange={handleProfileChange}
                    placeholder="How you appear in the app"
                  />
                  <Input
                    label="Phone Number"
                    name="phone"
                    value={profileData?.phone}
                    onChange={handleProfileChange}
                    placeholder="+968 XXXX XXXX"
                  />
                  <Input
                    label="Department"
                    name="department"
                    value={profileData?.department}
                    onChange={handleProfileChange}
                    placeholder="e.g. Engineering, Finance"
                  />
                  <Input
                    label="Job Title"
                    name="job_title"
                    value={profileData?.job_title}
                    onChange={handleProfileChange}
                    placeholder="e.g. Senior Estimator"
                    className="md:col-span-2"
                  />
                </div>

                {/* Role Field — synced from Roles & Permissions master page */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-foreground">Assigned Role(s)</label>
                    <span className="flex items-center gap-1 text-xs text-success">
                      <Icon name="Link" size={11} />
                      Synced from Roles &amp; Permissions
                    </span>
                  </div>
                  {rbacLoading ? (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-muted/20">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading role data...</span>
                    </div>
                  ) : rbacRoleKeys?.length > 0 ? (
                    <div className="space-y-2">
                      {rbacRoleKeys?.map(rk => {
                        const rc = RBAC_ROLE_CONFIG?.[rk];
                        if (!rc) return null;
                        return (
                          <div key={rk} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${rc?.border || 'border-border'} ${rc?.bg}/30 bg-muted/10`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rc?.bg}`}>
                              <Icon name={rc?.icon} size={16} className={rc?.color} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${rc?.color}`}>{rc?.label}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${rc?.bg} ${rc?.color}`}>
                                  {rk}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">{rc?.description}</p>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                              <Icon name="Lock" size={12} />
                              <span>Assigned by Admin</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-border ${legacyRoleConf?.bg}/30 bg-muted/20`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${legacyRoleConf?.bg}`}>
                        <Icon name={legacyRoleConf?.icon} size={16} className={legacyRoleConf?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${legacyRoleConf?.color}`}>{legacyRoleConf?.label}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${legacyRoleConf?.bg} ${legacyRoleConf?.color}`}>
                            <Icon name={legacyRoleConf?.icon} size={10} />
                            {userProfile?.role || 'viewer'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{legacyRoleConf?.description}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                        <Icon name="Lock" size={12} />
                        <span>Assigned by Admin</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-muted/30 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Icon name="Mail" size={16} className="text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground">Email Address (cannot be changed)</div>
                    <div className="text-sm font-medium text-foreground">{user?.email}</div>
                  </div>
                </div>

                {profileError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-error/10 border border-error/20">
                    <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{profileError}</p>
                  </div>
                )}
                {profileSuccess && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-success/10 border border-success/20">
                    <Icon name="CheckCircle" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-success">{profileSuccess}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="submit" loading={profileLoading} disabled={profileLoading}>
                    Save Profile
                  </Button>
                </div>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                <div>
                  <h2 className="text-base font-semibold text-foreground mb-1">Change Password</h2>
                  <p className="text-sm text-muted-foreground">Choose a strong password with at least 8 characters.</p>
                </div>

                <div className="space-y-4 max-w-md">
                  <div className="relative">
                    <Input
                      type={showPasswords?.current ? 'text' : 'password'}
                      label="Current Password"
                      name="current_password"
                      value={passwordData?.current_password}
                      onChange={handlePasswordChange}
                      placeholder="Enter current password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, current: !p?.current }))}
                      className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      <Icon name={showPasswords?.current ? 'EyeOff' : 'Eye'} size={18} />
                    </button>
                  </div>

                  <div className="relative">
                    <Input
                      type={showPasswords?.new ? 'text' : 'password'}
                      label="New Password"
                      name="new_password"
                      value={passwordData?.new_password}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, new: !p?.new }))}
                      className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      <Icon name={showPasswords?.new ? 'EyeOff' : 'Eye'} size={18} />
                    </button>
                  </div>

                  <div className="relative">
                    <Input
                      type={showPasswords?.confirm ? 'text' : 'password'}
                      label="Confirm New Password"
                      name="confirm_password"
                      value={passwordData?.confirm_password}
                      onChange={handlePasswordChange}
                      placeholder="Re-enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, confirm: !p?.confirm }))}
                      className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      <Icon name={showPasswords?.confirm ? 'EyeOff' : 'Eye'} size={18} />
                    </button>
                  </div>
                </div>

                {passwordData?.new_password && (
                  <div className="max-w-md space-y-1.5">
                    {[
                      { label: 'At least 8 characters', met: passwordData?.new_password?.length >= 8 },
                      { label: 'Contains uppercase letter', met: /[A-Z]/?.test(passwordData?.new_password) },
                      { label: 'Contains a number', met: /\d/?.test(passwordData?.new_password) },
                    ]?.map(hint => (
                      <div key={hint?.label} className="flex items-center gap-2">
                        <Icon
                          name={hint?.met ? 'CheckCircle2' : 'Circle'}
                          size={14}
                          color={hint?.met ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                        />
                        <span className={`text-xs ${hint?.met ? 'text-success' : 'text-muted-foreground'}`}>
                          {hint?.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {passwordError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-error/10 border border-error/20 max-w-md">
                    <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{passwordError}</p>
                  </div>
                )}
                {passwordSuccess && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-success/10 border border-success/20 max-w-md">
                    <Icon name="CheckCircle" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-success">{passwordSuccess}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="submit" loading={passwordLoading} disabled={passwordLoading}>
                    Update Password
                  </Button>
                </div>
              </form>
            )}

            {/* Auth Preferences Tab */}
            {activeTab === 'preferences' && (
              <form onSubmit={handleAuthPrefsSubmit} className="space-y-6">
                <div>
                  <h2 className="text-base font-semibold text-foreground mb-1">Authentication Preferences</h2>
                  <p className="text-sm text-muted-foreground">Control how you sign in and manage your session security.</p>
                </div>

                <div className="flex items-start justify-between gap-4 p-4 bg-muted/20 rounded-xl border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Bell" size={18} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">Login Notifications</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Receive email alerts when a new sign-in is detected on your account.</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAuthPrefsChange('email_notifications', !authPrefs?.email_notifications)}
                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-smooth focus:outline-none ${authPrefs?.email_notifications ? 'bg-primary' : 'bg-muted'}`}
                    aria-checked={authPrefs?.email_notifications}
                    role="switch"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-smooth ${authPrefs?.email_notifications ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="flex items-start justify-between gap-4 p-4 bg-muted/20 rounded-xl border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="ShieldCheck" size={18} color="var(--color-accent)" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">Two-Factor Authentication</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Add an extra layer of security to your account with 2FA.</div>
                      {authPrefs?.two_factor_enabled && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-success font-medium">
                          <Icon name="CheckCircle2" size={12} /> Enabled
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAuthPrefsChange('two_factor_enabled', !authPrefs?.two_factor_enabled)}
                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-smooth focus:outline-none ${authPrefs?.two_factor_enabled ? 'bg-primary' : 'bg-muted'}`}
                    aria-checked={authPrefs?.two_factor_enabled}
                    role="switch"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-smooth ${authPrefs?.two_factor_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="p-4 bg-muted/20 rounded-xl border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon name="Info" size={18} className="text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-foreground">Account Details</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">User ID:</span> {user?.id?.slice(0, 8)}...
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Last Sign In:</span>{' '}
                        {user?.last_sign_in_at ? new Date(user.last_sign_in_at)?.toLocaleString() : 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Provider:</span>{' '}
                        {user?.app_metadata?.provider || 'email'}
                      </div>
                    </div>
                  </div>
                </div>

                {authPrefsError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-error/10 border border-error/20">
                    <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{authPrefsError}</p>
                  </div>
                )}
                {authPrefsSuccess && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-success/10 border border-success/20">
                    <Icon name="CheckCircle" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-success">{authPrefsSuccess}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="submit" loading={authPrefsLoading} disabled={authPrefsLoading}>
                    Save Preferences
                  </Button>
                </div>
              </form>
            )}

            {/* My Permissions Tab — live data from Roles & Permissions master page */}
            {activeTab === 'my-permissions' && (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-base font-semibold text-foreground mb-1">My Permissions</h2>
                    <p className="text-sm text-muted-foreground">
                      Your current access rights, automatically synced from the Roles &amp; Permissions master page.
                      Any changes made by an Admin are reflected here instantly.
                    </p>
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-success whitespace-nowrap flex-shrink-0 mt-1">
                    <Icon name="RefreshCw" size={12} />
                    Live sync
                  </span>
                </div>

                {/* Role summary */}
                <div className="flex flex-wrap gap-2">
                  {rbacRoleKeys?.length > 0 ? (
                    rbacRoleKeys?.map(rk => {
                      const rc = RBAC_ROLE_CONFIG?.[rk];
                      if (!rc) return null;
                      return (
                        <div key={rk} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${rc?.border || 'border-border'} ${rc?.bg}`}>
                          <Icon name={rc?.icon} size={14} className={rc?.color} />
                          <span className={`text-sm font-semibold ${rc?.color}`}>{rc?.label}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-border ${legacyRoleConf?.bg}`}>
                      <Icon name={legacyRoleConf?.icon} size={14} className={legacyRoleConf?.color} />
                      <span className={`text-sm font-semibold ${legacyRoleConf?.color}`}>{legacyRoleConf?.label}</span>
                    </div>
                  )}
                </div>

                {rbacLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="ml-3 text-sm text-muted-foreground">Loading permissions...</span>
                  </div>
                ) : isRbacAdmin ? (
                  /* Admin sees a special full-access banner */
                  (<div className="flex items-start gap-4 p-5 rounded-xl bg-violet-500/10 border border-violet-500/30">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="ShieldCheck" size={20} className="text-violet-500" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-violet-500 mb-1">Full System Access</div>
                      <p className="text-xs text-muted-foreground">
                        As an Admin, you have unrestricted access to all modules and features. Individual permission toggles do not apply to your account.
                      </p>
                    </div>
                  </div>)
                ) : accessibleModules?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                      <Icon name="ShieldOff" size={22} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground">No permissions assigned</p>
                    <p className="text-xs text-muted-foreground mt-1">Contact your administrator to assign a role with the appropriate access rights.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {accessibleModules?.map(([moduleKey, perms]) => {
                      const modConf = MODULE_DISPLAY?.[moduleKey] || { label: moduleKey, icon: 'Box', color: 'text-muted-foreground', bg: 'bg-muted' };
                      const grantedPerms = Object.entries(perms || {})?.filter(([, v]) => v === true)?.map(([k]) => k);
                      if (grantedPerms?.length === 0) return null;
                      return (
                        <div key={moduleKey} className="border border-border rounded-xl overflow-hidden">
                          <div className={`flex items-center gap-3 px-4 py-3 ${modConf?.bg}/30 border-b border-border`}>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${modConf?.bg}`}>
                              <Icon name={modConf?.icon} size={14} className={modConf?.color} />
                            </div>
                            <span className="text-sm font-semibold text-foreground">{modConf?.label}</span>
                            <span className="ml-auto text-xs text-muted-foreground">{grantedPerms?.length} permission{grantedPerms?.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="px-4 py-3 flex flex-wrap gap-2">
                            {grantedPerms?.map(pk => (
                              <span key={pk} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                                <Icon name="Check" size={10} />
                                {PERM_LABELS?.[pk] || pk}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30 border border-border mt-2">
                  <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Permissions are managed centrally in the <span className="font-semibold text-foreground">Roles &amp; Permissions</span> page.
                    If you believe your access is incorrect, contact your system administrator.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add User Modal */}
        {addUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="UserPlus" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Add New User</h2>
                    <p className="text-xs text-muted-foreground">Create a new user account with role assignment</p>
                  </div>
                </div>
                <button onClick={() => setAddUserModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={16} className="text-muted-foreground" />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Full Name <span className="text-error">*</span></label>
                  <input
                    type="text"
                    value={addUserData?.full_name}
                    onChange={e => setAddUserData(prev => ({ ...prev, full_name: e?.target?.value }))}
                    placeholder="Enter full name"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Email Address <span className="text-error">*</span></label>
                  <input
                    type="email"
                    value={addUserData?.email}
                    onChange={e => setAddUserData(prev => ({ ...prev, email: e?.target?.value }))}
                    placeholder="user@example.com"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Phone <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                  <input
                    type="tel"
                    value={addUserData?.phone}
                    onChange={e => setAddUserData(prev => ({ ...prev, phone: e?.target?.value }))}
                    placeholder="+968 XXXX XXXX"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Password <span className="text-error">*</span></label>
                  <input
                    type="password"
                    value={addUserData?.password}
                    onChange={e => setAddUserData(prev => ({ ...prev, password: e?.target?.value }))}
                    placeholder="Min. 8 characters"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Assign Role <span className="text-error">*</span></label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(LEGACY_ROLE_CONFIG)?.map(([roleKey, conf]) => (
                      <button
                        key={roleKey}
                        type="button"
                        onClick={() => setAddUserData(prev => ({ ...prev, role: roleKey }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                          addUserData?.role === roleKey
                            ? `border-primary bg-primary/5`
                            : 'border-border hover:border-primary/40 bg-background'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${conf?.bg}`}>
                          <Icon name={conf?.icon} size={16} className={conf?.color} />
                        </div>
                        <span className={`text-xs font-semibold ${addUserData?.role === roleKey ? 'text-primary' : 'text-foreground'}`}>{conf?.label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{LEGACY_ROLE_CONFIG?.[addUserData?.role]?.description}</p>
                </div>

                {addUserError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-error/10 border border-error/20">
                    <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{addUserError}</p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAddUserModal(false)}>Cancel</Button>
                  <Button type="submit" loading={addUserLoading} disabled={addUserLoading} iconName="UserPlus" iconPosition="left">
                    Create User
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="UserCog" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Edit User</h2>
                    <p className="text-xs text-muted-foreground">{editUserData?.email}</p>
                  </div>
                </div>
                <button onClick={() => setEditUserModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={16} className="text-muted-foreground" />
                </button>
              </div>

              <form onSubmit={handleEditUser} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Full Name <span className="text-error">*</span></label>
                  <input
                    type="text"
                    value={editUserData?.full_name}
                    onChange={e => setEditUserData(prev => ({ ...prev, full_name: e?.target?.value }))}
                    placeholder="Enter full name"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Email Address</label>
                  <input
                    type="email"
                    value={editUserData?.email}
                    disabled
                    className="w-full h-10 px-3 rounded-lg border border-border bg-muted/40 text-sm text-muted-foreground cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Phone <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                  <input
                    type="tel"
                    value={editUserData?.phone}
                    onChange={e => setEditUserData(prev => ({ ...prev, phone: e?.target?.value }))}
                    placeholder="+968 XXXX XXXX"
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Role</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(LEGACY_ROLE_CONFIG)?.map(([roleKey, conf]) => (
                      <button
                        key={roleKey}
                        type="button"
                        onClick={() => setEditUserData(prev => ({ ...prev, role: roleKey }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                          editUserData?.role === roleKey
                            ? `border-primary bg-primary/5`
                            : 'border-border hover:border-primary/40 bg-background'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${conf?.bg}`}>
                          <Icon name={conf?.icon} size={16} className={conf?.color} />
                        </div>
                        <span className={`text-xs font-semibold ${editUserData?.role === roleKey ? 'text-primary' : 'text-foreground'}`}>{conf?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Status</label>
                  <div className="flex gap-3">
                    {[{ value: true, label: 'Active', icon: 'CheckCircle2', color: 'text-success', bg: 'bg-success/10', border: 'border-success' },
                      { value: false, label: 'Inactive', icon: 'XCircle', color: 'text-error', bg: 'bg-error/10', border: 'border-error' }]?.map(opt => (
                      <button
                        key={String(opt?.value)}
                        type="button"
                        onClick={() => setEditUserData(prev => ({ ...prev, is_active: opt?.value }))}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                          editUserData?.is_active === opt?.value
                            ? `${opt?.border} ${opt?.bg} ${opt?.color}`
                            : 'border-border bg-background text-muted-foreground hover:border-border/80'
                        }`}
                      >
                        <Icon name={opt?.icon} size={15} />
                        {opt?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {editUserError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-error/10 border border-error/20">
                    <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{editUserError}</p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditUserModal(false)}>Cancel</Button>
                  <Button type="submit" loading={editUserLoading} disabled={editUserLoading} iconName="Save" iconPosition="left">
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmModal && deleteTargetUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="p-6 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center mx-auto">
                  <Icon name="Trash2" size={24} className="text-error" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">Delete User</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Are you sure you want to remove <span className="font-semibold text-foreground">{deleteTargetUser?.full_name || deleteTargetUser?.email}</span> from the system?
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">This action will deactivate the user account. This cannot be undone.</p>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setDeleteConfirmModal(false); setDeleteTargetUser(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 bg-error hover:bg-error/90 text-white border-error"
                    loading={deleteLoading}
                    disabled={deleteLoading}
                    onClick={handleDeleteUser}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete User
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AccountManagement;
